/**
 * PatientContinuationPage – 継続受診者登録 (Continuation)
 * A2-only: list continuation visits, search by date/sort/patient, edit/delete/print.
 * Menu entry is shown only for A2 in Sidebar.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import {
  AppButton,
  AppPagination,
  AppRadio,
  AppRadioGroup,
  AppSpin,
  AppTable,
  AppTableActionButton,
  AppTextField,
} from '@/components/atoms';
import {
  CollapsibleCard,
  ContinuationReceiptEditModal,
  PatientEditLink,
} from '@/components/molecules';
import { RA2NDatePicker } from '@/components/organisms';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import {
  useContinuationInit,
  useContinuationList,
  useContinuationAdd,
  useContinuationDelete,
  useContinuationPrintReset,
  useContinuationListPdf,
} from '@/hooks/main/useContinuationList';
import {
  searchContinuationPatientId,
  type ContinuationParam,
  type ContinuationListRow,
} from '@/services/main/continuation/continuationService';
import { getApiErrorMessage } from '@/utils/apiError';
import { convertSeirekiWrap, convertWarekiWrap } from '@/utils/wareki';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import VisitClinicPatientSearchModal from '@/pages/main/visitClinic/components/VisitClinicPatientSearchModal';
import type { PatientRow as VisitClinicPatientRow } from '@/pages/main/visitClinic/components/VisitClinicPatientSearchModal';
import { Checkbox, Table } from 'antd';
import BottomBar from '@/components/organisms/Footer';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import type { ColumnsType } from 'antd/es/table';
import { DELETE_CONFIRM_DATA_MESSAGE } from '@/constants/messages';

type SortValue = 0 | 1 | 2;

const PAGE_SIZE = 20;
const LOCKED_DELETE_MESSAGE = 'この受診者はロックされているため、削除できません。';

const PatientContinuationPage: React.FC = () => {
  const toast = useAppToast();
  const { confirm, error } = useAppModal();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenant, clinicNumber } = useClinicContext();
  const siteType = tenant;

  // Restore from URL params on mount
  const urlTreatment = searchParams.get('treatment') || '';
  const urlSort = searchParams.get('sort');
  const urlRestored = useRef(false);
  const initialSearchAppliedRef = useRef(false);

  const [datePickerValue, setDatePickerValue] = useState<string>(() => {
    if (urlTreatment) {
      const normalized = urlTreatment.replace(/-/g, '/');
      return /^\d{4}\/\d{2}\/\d{2}$/.test(normalized)
        ? convertWarekiWrap(normalized)
        : urlTreatment;
    }
    const today = new Date();
    const y = String(today.getFullYear());
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return convertWarekiWrap(`${y}/${m}/${d}`);
  });
  const datePickerInitializedRef = useRef(false);
  const [sort, setSort] = useState<SortValue>((urlSort != null ? Number(urlSort) : 2) as SortValue);
  const searchForm = useTrimmedForm<{ sKeyword: string }>({ defaultValues: { sKeyword: '' } });
  const [searchApplied, setSearchApplied] = useState(false);
  const [appliedSearch, setAppliedSearch] = useState<{ treatment: string; sort: SortValue } | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);

  // Auto-search if URL has params (back from another page)
  useEffect(() => {
    if (!urlRestored.current && urlTreatment) {
      urlRestored.current = true;
      const normalized = urlTreatment.replace(/-/g, '/');
      const wareki = /^\d{4}\/\d{2}\/\d{2}$/.test(normalized)
        ? convertWarekiWrap(normalized)
        : urlTreatment;
      setAppliedSearch({
        treatment: wareki,
        sort: (urlSort != null ? Number(urlSort) : 2) as SortValue,
      });
      setSearchApplied(true);
    }
  }, [urlTreatment, urlSort]);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchModalKeyword, setSearchModalKeyword] = useState('');
  const [dailyReceiptModalRecord, setDailyReceiptModalRecord] =
    useState<ContinuationListRow | null>(null);

  const sidebarOpen = useSidebarOpen();

  // Print options state
  const [printClinicName, setPrintClinicName] = useState(false);
  const [printManagerName, setPrintManagerName] = useState(false);
  const [printMassagerName, setPrintMassagerName] = useState(false);
  const [printPatientNumber, setPrintPatientNumber] = useState(true);
  const [printDate, setPrintDate] = useState(true);
  const [printDateValue, setPrintDateValue] = useState<string>(() => {
    const today = new Date();
    const y = String(today.getFullYear());
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return convertWarekiWrap(`${y}/${m}/${d}`);
  });
  const [printClinicAddress, setPrintClinicAddress] = useState(false);
  const [printManagerAddress, setPrintManagerAddress] = useState(false);
  const [printMassagerAddress, setPrintMassagerAddress] = useState(false);

  const queryClient = useQueryClient();
  const { data: initData, isLoading: initLoading } = useContinuationInit();
  const param: ContinuationParam | undefined = initData?.param;

  useEffect(() => {
    if (!datePickerInitializedRef.current && param) {
      // Initialize print options from BE defaults if is_init=1
      if (param.is_init === 1) {
        if (param.option_1 === 1) setPrintManagerName(true);
        if (param.option_2 === 1) setPrintManagerAddress(true);
      }
      datePickerInitializedRef.current = true;
    }
  }, [param, urlTreatment]);

  const listParams =
    searchApplied && appliedSearch
      ? {
          search_treatment: appliedSearch.treatment,
          search_term_type: 1,
          sort: appliedSearch.sort,
          page: currentPage,
          per_page: PAGE_SIZE,
        }
      : null;

  const { data: listData, isLoading: listLoading } = useContinuationList(listParams);
  const addMutation = useContinuationAdd();
  const deleteMutation = useContinuationDelete();
  const printResetMutation = useContinuationPrintReset();
  const listPdfMutation = useContinuationListPdf({ invalidateOnSuccess: false });

  const rawList = listData?.list ?? [];
  const list = rawList;
  // Server returns totals over the full result set so the summary stays correct
  // across pages. Fall back to client-side reduction for legacy responses.
  const totalMadoguchiPrice = useMemo(
    () =>
      listData?.totals
        ? listData.totals.madoguchi
        : list.reduce((sum, r) => sum + (Number(r.madoguchi_price) || 0), 0),
    [listData?.totals, list]
  );
  const totalTreatmentPrice = useMemo(
    () =>
      listData?.totals
        ? listData.totals.treatment
        : list.reduce((sum, r) => sum + (Number(r.treatment_price) || 0), 0),
    [listData?.totals, list]
  );
  const totalPatientPrice = useMemo(
    () =>
      listData?.totals
        ? listData.totals.patient
        : list.reduce((sum, r) => sum + (Number(r.patient_price) || 0), 0),
    [listData?.totals, list]
  );

  const totalCount = listData?.pagination?.total ?? list.length;

  const freeMedicalsForModal = useMemo(
    () => listData?.free_medicals ?? initData?.free_medicals ?? [],
    [listData?.free_medicals, initData?.free_medicals]
  );

  const validateTreatmentDate = useCallback(
    (treatmentValue = datePickerValue): string | null => {
      if (!treatmentValue.trim()) {
        error({ title: '日付を入力してください。', okText: 'OK' });
        return null;
      }
      const seirekiSlash = convertSeirekiWrap(treatmentValue);
      const today = new Date();
      const todayStr = `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`;
      if (seirekiSlash > todayStr) {
        error({ title: '現在日より未来を指定することはできません。', okText: 'OK' });
        return null;
      }
      return seirekiSlash;
    },
    [datePickerValue, error]
  );

  const runSearch = useCallback(
    (treatmentValue: string) => {
      const seirekiSlash = validateTreatmentDate(treatmentValue);
      if (!seirekiSlash) return;
      setCurrentPage(1);
      const treatment = treatmentValue || param?.search_treatment || '';
      setAppliedSearch({ treatment, sort });
      setSearchApplied(true);
      const seireki = seirekiSlash.replace(/\//g, '-');
      setSearchParams({ treatment: seireki, sort: String(sort) }, { replace: true });
    },
    [validateTreatmentDate, param?.search_treatment, sort, setSearchParams]
  );

  const handleSearch = useCallback(() => {
    runSearch(datePickerValue);
  }, [runSearch, datePickerValue]);

  useEffect(() => {
    if (urlTreatment || searchApplied || initialSearchAppliedRef.current || !datePickerValue) {
      return;
    }
    initialSearchAppliedRef.current = true;
    runSearch(datePickerValue);
  }, [datePickerValue, runSearch, searchApplied, urlTreatment]);

  const handleTreatmentDateChange = useCallback((value: string) => {
    setDatePickerValue(value || '');
  }, []);

  const handleTreatmentDateCommit = useCallback(
    (value: string) => {
      const nextValue = value || '';
      setDatePickerValue(nextValue);
      if (nextValue) runSearch(nextValue);
    },
    [runSearch]
  );

  const handleSelectPatient = useCallback(
    (patient: VisitClinicPatientRow) => {
      const searchTreatment = datePickerValue || param?.search_treatment || '';
      addMutation.mutate(
        { patientId: patient.patient_id, searchTreatment, sortRadio: String(sort) },
        {
          onSuccess: data => {
            if (data.already_registered) {
              toast.error('この受診者は、すでに施術登録済みです。');
            } else {
              toast.success('継続受診者を登録しました。');
            }
            searchForm.setValue('sKeyword', '');
            setSearchModalKeyword('');
            setCurrentPage(1);
            const treatment = datePickerValue || param?.search_treatment || '';
            setAppliedSearch({ treatment, sort });
            setSearchApplied(true);
          },
          onError: err => {
            const message = getApiErrorMessage(err, '登録に失敗しました。');
            const needsNavigateConfirm = message.includes('施術入力画面に移動しますか');
            if (needsNavigateConfirm) {
              confirm({
                title: message,
                okText: 'OK',
                cancelText: 'キャンセル',
                onOk: () =>
                  navigate(`/${tenant}/visit_clinic/schedule?patient_id=${patient.patient_id}`, {
                    replace: true,
                  }),
              });
            } else {
              toast.error(message);
            }
          },
        }
      );
    },
    [
      datePickerValue,
      param?.search_treatment,
      sort,
      addMutation,
      searchForm,
      toast,
      confirm,
      navigate,
      tenant,
    ]
  );

  const handlePatientSearchClick = useCallback(async () => {
    if (!validateTreatmentDate()) return;
    const sKeyword = searchForm.getValues('sKeyword').trim();
    if (sKeyword && /^\d+$/.test(sKeyword) && tenant && clinicNumber) {
      try {
        const res = await searchContinuationPatientId(tenant, clinicNumber, sKeyword);
        if (res.response && res.patient_id) {
          handleSelectPatient({ patient_id: res.patient_id } as VisitClinicPatientRow);
          return;
        }
      } catch {
        // fall through to modal on lookup failure
      }
    }
    setSearchModalKeyword(sKeyword);
    setSearchModalOpen(true);
  }, [validateTreatmentDate, searchForm, tenant, clinicNumber, handleSelectPatient]);

  const handleDelete = useCallback(
    (record: ContinuationListRow) => {
      if (Number(record.is_locked ?? 0) === 1) {
        toast.error(LOCKED_DELETE_MESSAGE);
        return;
      }

      confirm({
        title: DELETE_CONFIRM_DATA_MESSAGE,
        okText: 'OK',
        cancelText: 'キャンセル',
        okButtonProps: { danger: true },
        onOk: () => deleteMutation.mutate({ delete_id: record.id }),
      });
    },
    [confirm, deleteMutation, toast]
  );

  const handlePrintReset = useCallback(
    (visitId: number) => {
      confirm({
        title: '印刷済みをリセットしますか？',
        okText: 'OK',
        cancelText: 'キャンセル',
        onOk: () => printResetMutation.mutate(visitId),
      });
    },
    [confirm, printResetMutation]
  );

  const openDailyReceiptModal = useCallback((record: ContinuationListRow) => {
    setDailyReceiptModalRecord(record);
  }, []);

  const handleListPrint = useCallback(() => {
    const pdfWindow = window.open('about:blank', '_blank');
    const treatment = appliedSearch?.treatment || datePickerValue || param?.search_treatment || '';
    const sortVal = appliedSearch?.sort ?? sort;
    listPdfMutation.mutate(
      {
        search_treatment: treatment,
        search_term_type: 1,
        sort: sortVal,
      },
      {
        onSuccess: blob => {
          const file = new File([blob], 'visit_clinic_list.pdf', { type: 'application/pdf' });
          const url = URL.createObjectURL(file);
          if (pdfWindow) {
            pdfWindow.location.href = url;
          } else {
            window.open(url, '_blank');
          }
        },
        onError: err => {
          if (pdfWindow) pdfWindow.close();
          toast.error(getApiErrorMessage(err, '印刷に失敗しました。'));
        },
      }
    );
  }, [appliedSearch, datePickerValue, param?.search_treatment, sort, list, listPdfMutation, toast]);

  const columns: ColumnsType<ContinuationListRow> = useMemo(
    () => [
      {
        title: '受診者番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 80,
        render: (val: string | number, record: ContinuationListRow) => (
          <PatientEditLink patientId={record.patient_id} value={val} />
        ),
      },
      { title: '受診者名漢字', dataIndex: 'name', key: 'name', width: 120, ellipsis: true },
      {
        title: '受診者名仮名',
        dataIndex: 'name_kana',
        key: 'name_kana',
        width: 120,
        ellipsis: true,
      },
      { title: '保険種別', dataIndex: 'insurer_kind_name', key: 'insurer_kind_name', width: 100 },
      {
        title: '負担割合',
        dataIndex: 'patient_cost_rate_str',
        key: 'patient_cost_rate_str',
        width: 80,
      },
      {
        title: '医療助成',
        dataIndex: 'medical_aid_name',
        key: 'medical_aid_name',
        width: 100,
        render: (val?: string) => val || '—',
      },
      {
        title: '初検',
        dataIndex: 'is_initial_inspection_fee_mark',
        key: 'is_initial_inspection_fee_mark',
        width: 50,
      },
      {
        title: '窓口料金',
        dataIndex: 'madoguchi_price',
        key: 'madoguchi_price',
        width: 90,
        align: 'right',
        render: (val: number | undefined) => (val != null ? Number(val).toLocaleString() : '—'),
      },
      {
        title: '施術料金',
        dataIndex: 'treatment_price',
        key: 'treatment_price',
        width: 90,
        align: 'right',
        render: (val: number | undefined) => (val != null ? Number(val).toLocaleString() : '—'),
      },
      {
        title: '領収金額',
        dataIndex: 'patient_price',
        key: 'patient_price',
        width: 90,
        align: 'right',
        render: (val: number | undefined) => (
          <span className="inline-block border border-gray-300 rounded bg-gray-50 px-2 py-0.5 min-w-[60px] text-right">
            {val != null ? Number(val).toLocaleString() : '—'}
          </span>
        ),
      },
      {
        title: '印刷',
        dataIndex: 'print_invoice_mark',
        key: 'print_invoice_mark',
        width: 50,
        render: (val: string | undefined, record: ContinuationListRow) =>
          val && val !== '' ? (
            <AppButton
              type="link"
              className="h-auto p-0 text-blue-600 hover:underline"
              onClick={() => handlePrintReset(record.id)}
            >
              {val}
            </AppButton>
          ) : null,
      },
      {
        title: '日別領収',
        key: 'daily_receipt',
        width: 60,
        render: (_: unknown, record: ContinuationListRow) => {
          const searchYearmonth =
            record.visit_datetime_from && record.visit_datetime_from.length >= 7
              ? record.visit_datetime_from.slice(0, 7)
              : '';
          return (
            <AppButton
              type="primary"
              size="small"
              className="w-full"
              onClick={() => searchYearmonth && openDailyReceiptModal(record)}
              disabled={!searchYearmonth}
            >
              編集
            </AppButton>
          );
        },
      },
      {
        title: '操作',
        key: 'action',
        width: 140,
        align: 'center' as const,
        className: 'whitespace-nowrap',
        fixed: 'right',
        render: (_: unknown, record: ContinuationListRow) => {
          const searchYearmonth =
            record.move_to_visit_clinic_month ?? record.visit_datetime_from?.slice(0, 7) ?? '';
          return (
            <div className="flex justify-center gap-1 flex-nowrap">
              <AppTableActionButton
                action="edit"
                onClick={() =>
                  searchYearmonth &&
                  navigate(`/${tenant}/visit_clinic/edit/${record.patient_id}/${searchYearmonth}`)
                }
                disabled={!searchYearmonth}
              >
                施術
              </AppTableActionButton>
              <AppTableActionButton
                action="delete"
                onClick={() => handleDelete(record)}
              >
                削除
              </AppTableActionButton>
            </div>
          );
        },
      },
    ],
    [navigate, tenant, handleDelete, handlePrintReset, openDailyReceiptModal]
  );

  const isLoading = initLoading || (searchApplied && listLoading);

  return (
    <div>
      <CollapsibleCard title="検索条件" className="mb-4">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-wrap items-center">
              <label className="mr-2 text-sm w-[150px]">
                <span className="font-medium">施術日</span>
              </label>
              <RA2NDatePicker
                value={datePickerValue || undefined}
                onChange={handleTreatmentDateChange}
                placeholder="元号00年00月00日"
                startDate="1980-01-01"
                endDate="2057-12-31"
                className="w-[200px]"
                readOnlyInput
                onCalendarCommit={handleTreatmentDateCommit}
              />
            </div>
            <AppRadioGroup
              value={sort}
              onChange={e => setSort(Number(e.target.value) as SortValue)}
              className="flex items-center gap-4"
            >
              <AppRadio value={0}>受診者番号順</AppRadio>
              <AppRadio value={1}>仮名順</AppRadio>
              <AppRadio value={2}>入力順</AppRadio>
            </AppRadioGroup>
            <AppButton type="primary" onClick={handleSearch}>
              <i className="mr-1 fa fa-search" aria-hidden /> 検索
            </AppButton>
          </div>

          <hr className="my-2 border-gray-200" />

          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center">
              <label className="mr-2 text-sm w-[150px]">
                <span className="font-medium">受診者仮名</span>
              </label>
              <Controller
                name="sKeyword"
                control={searchForm.control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    placeholder="受診者名"
                    className="w-[200px]"
                    maxLength={255}
                  />
                )}
              />
            </div>
            <AppButton type="primary" onClick={handlePatientSearchClick}>
              <i className="mr-1 fa fa-search" aria-hidden /> 追加
            </AppButton>
          </div>
        </div>
      </CollapsibleCard>

      {searchApplied && (
        <CollapsibleCard>
          <AppSpin spinning={isLoading}>
            {!listLoading && (
              <>
                <AppTable<ContinuationListRow>
                  rowKey="id"
                  columns={columns}
                  dataSource={list}
                  scroll={{ x: 1200 }}
                  recordCount={totalCount}
                  onRow={(record: ContinuationListRow) => ({
                    className: record.is_report_only ? 'report-only-row' : '',
                  })}
                  summary={() => (
                    <Table.Summary fixed>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={7} className="text-center">
                          合計
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7} className="text-right">
                          {totalMadoguchiPrice.toLocaleString()}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={8} className="text-right">
                          {totalTreatmentPrice.toLocaleString()}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={9} className="text-right">
                          {totalPatientPrice.toLocaleString()}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={10} colSpan={3} />
                      </Table.Summary.Row>
                    </Table.Summary>
                  )}
                />
                <AppPagination
                  className="mt-3"
                  current={currentPage}
                  total={totalCount}
                  pageSize={PAGE_SIZE}
                  onChange={setCurrentPage}
                />
              </>
            )}
          </AppSpin>
        </CollapsibleCard>
      )}

      <VisitClinicPatientSearchModal
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        keyword={searchModalKeyword}
        onSelectPatient={handleSelectPatient}
      />

      <ContinuationReceiptEditModal
        isOpen={!!dailyReceiptModalRecord}
        onClose={() => setDailyReceiptModalRecord(null)}
        record={dailyReceiptModalRecord}
        freeMedicals={freeMedicalsForModal}
        siteType={siteType ?? ''}
        clinicNumber={clinicNumber ?? ''}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['continuation'] })}
        printSettings={{
          showPatientNumber: printPatientNumber,
          showDate: printDate,
          dateValue: printDateValue,
          showClinicName: printClinicName,
          showClinicAddress: printClinicAddress,
          showManagerName: printManagerName,
          showManagerAddress: printManagerAddress,
          showMassagerName: printMassagerName,
          showMassagerAddress: printMassagerAddress,
        }}
      />

      {searchApplied && (
        <CollapsibleCard title="印刷オプション" className="mt-4">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            {/* Left column */}
            <div className="space-y-3">
              <div>
                <Checkbox
                  checked={printClinicName}
                  onChange={e => setPrintClinicName(e.target.checked)}
                >
                  治療院基本情報の治療院名を表示
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={printManagerName}
                  onChange={e => setPrintManagerName(e.target.checked)}
                >
                  施術管理者の名前を表示
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={printMassagerName}
                  onChange={e => setPrintMassagerName(e.target.checked)}
                >
                  施術師の名前を表示
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={printPatientNumber}
                  onChange={e => setPrintPatientNumber(e.target.checked)}
                >
                  受診者番号を表示
                </Checkbox>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={printDate} onChange={e => setPrintDate(e.target.checked)}>
                  日付を表示
                </Checkbox>
                <RA2NDatePicker
                  value={printDateValue || undefined}
                  onChange={v => setPrintDateValue(v || '')}
                  placeholder="元号00年00月00日"
                  startDate="1980-01-01"
                  endDate="2057-12-31"
                  className="w-[180px]"
                />
              </div>
            </div>
            {/* Right column */}
            <div className="space-y-3">
              <div>
                <Checkbox
                  checked={printClinicAddress}
                  onChange={e => setPrintClinicAddress(e.target.checked)}
                >
                  治療院基本情報の住所と電話番号を表示
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={printManagerAddress}
                  onChange={e => setPrintManagerAddress(e.target.checked)}
                >
                  施術管理者の住所・電話番号を表示
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={printMassagerAddress}
                  onChange={e => setPrintMassagerAddress(e.target.checked)}
                >
                  施術師拠点の住所・電話番号を表示
                </Checkbox>
              </div>
              {/* <div className="pt-2">
                <span className="text-sm text-gray-500">※施術報告書交付料は未対応です。</span>
              </div> */}
            </div>
          </div>
        </CollapsibleCard>
      )}

      <BottomBar
        variant="action"
        visible={searchApplied && list.length > 0}
        fixed
        className={sidebarOpen ? 'left-60' : 'left-0'}
        left={
          <AppButton
            type="primary"
            className="rounded"
            onClick={handleListPrint}
            loading={listPdfMutation.isPending}
          >
            一覧印刷
          </AppButton>
        }
      />
    </div>
  );
};

export default PatientContinuationPage;
